import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineXS } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useCloseDialog } from '@sorare/core/src/hooks/navigation/useCloseDialog';
import { useDialogParam } from '@sorare/core/src/hooks/navigation/useDialogParam';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { cart, glossary } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { regularMaterial } from '@sorare/core/src/style/utils';

import { CartPayment } from 'components/cart/CartPayment';
import { CartTotalSummary } from 'components/cart/CartTotalSummary';
import { CollectionUpsell } from 'components/cart/CollectionUpsell';
import { MainCartItems } from 'components/cart/MainCartItems';
import { SavedForLaterItems } from 'components/cart/SavedForLaterItems';
import { PlayerDrawer } from 'components/player/PlayerDrawer';

import { LazyCartPromos } from '../LazyCartPromos';
import type {
  CheckoutQuery,
  CheckoutQueryVariables,
} from './__generated__/index.graphql';

const Layout = styled(Horizontal).attrs({
  gap: 0,
})`
  height: 100%;
`;

const Cart = styled(Vertical).attrs({
  gap: 0,
})`
  width: 100%;
  height: 100%;
  @media ${laptopAndAbove} {
    border-right: 1px solid var(--c-nd-150);
  }
  overflow-y: auto;
  align-items: center;
`;

const CartContainer = styled(Vertical).attrs({
  gap: 3,
})`
  padding: var(--triple-unit) var(--double-unit);

  width: 100%;
  max-width: 882px;
`;

const Payment = styled.div`
  display: none;
  @media ${laptopAndAbove} {
    position: static;
    display: block;
    width: 437px;
    flex-shrink: 0;
    overflow-y: auto;
    height: 100%;
  }
`;

const PaymentContainer = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--double-unit);
  @media ${laptopAndAbove} {
    padding: var(--triple-unit) var(--double-unit);
  }
`;

const Footer = styled(Vertical).attrs({
  gap: 2,
})`
  ${regularMaterial}
  padding: var(--double-unit);
  @media ${laptopAndAbove} {
    display: none;
  }
`;

const DIALOG_KEY = DialogKey.checkout;

const CHECKOUT_QUERY = gql`
  query CheckoutQuery(
    $sport: Sport!
    $withCollectionPredictions: Boolean = true
  ) {
    currentUser {
      slug
      myCart(sport: $sport) {
        id
        mainCartItems {
          id
          ...MainCartItems_cartItem
        }
        savedForLaterCartItems {
          id
          ...SavedForLaterItems_cartItem
        }
        affectedCardCollections {
          id
          ...CollectionUpsell_CardCollectionScoreIncrease
        }
        price {
          ...useMonetaryAmount_monetaryAmount
        }
        ...CartPayment_cart
        ...LazyCartPromos_cart
      }
    }
  }
  ${MainCartItems.fragments.cartItem}
  ${SavedForLaterItems.fragments.cartItem}
  ${CollectionUpsell.fragments.cardCollectionScoreIncrease}
  ${CartPayment.fragments.cart}
  ${LazyCartPromos.fragments.cart}
  ${useMonetaryAmount.fragments.monetaryAmount}
` as TypedDocumentNode<CheckoutQuery, CheckoutQueryVariables>;

type LineupCard = NonNullable<
  ComponentProps<typeof MainCartItems>['lineupCards']
>[number];
type Props = {
  lineupCards?: LineupCard[];
  onClose?: () => void;
  onSuccess?: (anyCardsIds: string[]) => void;
};

export const Checkout = ({
  lineupCards,
  onClose: onCloseProps,
  onSuccess: onSuccessProps,
}: Props) => {
  const track = useEvents();

  const {
    isMarketAuthorizedIosApp,
    canOpenCartOpenPaymentBoxAndCloseCurrentWebview,
    isIosApp,
    postMessage,
  } = useIsMobileApp();
  const { toMonetaryAmount } = useMonetaryAmount();
  const sport = useDialogParam(DialogKey.checkout);
  const { up: laptopOrAbove } = useScreenSize('laptop');

  const [playerSlug, setPlayerSlug] = useState<string | null>(null);
  const [openPayment, setOpenPayment] = useState<boolean>(false);
  const closeDialog = useCloseDialog();

  const canCloseIosWebview =
    isMarketAuthorizedIosApp && canOpenCartOpenPaymentBoxAndCloseCurrentWebview;

  const closeIosWebview = () => {
    postMessage('closeCurrentWebview');
  };

  const onClose = () => {
    if (canCloseIosWebview) {
      closeIosWebview();
    }
    if (onCloseProps) {
      onCloseProps();
    } else {
      closeDialog(DIALOG_KEY);
    }
  };

  const onSuccess = (ids: string[]) => {
    if (onSuccessProps) {
      onSuccessProps?.(ids);
    } else if (canCloseIosWebview) {
      closeIosWebview();
    } else {
      closeDialog(DIALOG_KEY);
    }
  };

  const { data, loading } = useQuery<CheckoutQuery, CheckoutQueryVariables>(
    CHECKOUT_QUERY,
    {
      variables: {
        sport: (sport as Sport) || Sport.FOOTBALL,
      },
    }
  );
  const myCart = data?.currentUser?.myCart;
  const mainCartItems = data?.currentUser?.myCart?.mainCartItems || [];
  const savedForLaterItems =
    data?.currentUser?.myCart?.savedForLaterCartItems || [];
  const affectedCardCollections =
    data?.currentUser?.myCart?.affectedCardCollections || [];
  const price = myCart?.price;
  const isCartEmpty = !loading && !myCart?.mainCartItems?.length;

  const monetaryPrice = toMonetaryAmount(price);

  useEffect(() => {
    if (
      !!laptopOrAbove &&
      !isCartEmpty &&
      !!canOpenCartOpenPaymentBoxAndCloseCurrentWebview
    ) {
      postMessage('openPaymentBox');
    }
  }, [
    laptopOrAbove,
    postMessage,
    isCartEmpty,
    canOpenCartOpenPaymentBoxAndCloseCurrentWebview,
  ]);

  return (
    <>
      <Dialog
        open
        fullScreen
        onClose={!isIosApp ? onClose : undefined}
        title={
          <HeadlineXS className="text-center">
            <FormattedMessage {...cart.cart} />
          </HeadlineXS>
        }
        withDivider
        {...(!laptopOrAbove && {
          footer: (
            <Footer>
              {!!price && (
                <CartTotalSummary total={price} sport={myCart.sport} />
              )}
              <Button
                onClick={() => {
                  track('Mobile Click Checkout Intermediate Button', {
                    cartId: myCart?.id || '',
                    ethAmount: monetaryPrice?.eth,
                    eurAmount: monetaryPrice?.eur,
                    sport: myCart?.sport,
                  });
                  if (
                    isIosApp &&
                    canOpenCartOpenPaymentBoxAndCloseCurrentWebview
                  ) {
                    postMessage('openPaymentBox');
                  }
                  setOpenPayment(true);
                }}
                color="primary"
                size="medium"
                fullWidth
                disabled={!price || monetaryPrice.isZero}
              >
                <FormattedMessage
                  id="Cart.mobile.checkout.intermediate.button"
                  defaultMessage="Checkout"
                />
              </Button>
            </Footer>
          ),
        })}
      >
        <Layout>
          <Cart>
            {!!myCart && <LazyCartPromos cart={myCart} />}
            <CartContainer>
              <MainCartItems
                cartItems={mainCartItems}
                lineupCards={lineupCards}
              />
              <CollectionUpsell
                affectedCardCollections={affectedCardCollections}
              />
              <SavedForLaterItems
                cartItems={savedForLaterItems}
                onStatsClick={c => {
                  setPlayerSlug(c);
                }}
              />
            </CartContainer>
          </Cart>
          <Payment>
            {!!myCart && (
              <PaymentContainer>
                <CartPayment myCart={myCart} onSuccess={onSuccess} />
              </PaymentContainer>
            )}
          </Payment>
        </Layout>
      </Dialog>
      <Dialog
        open={openPayment}
        onBack={() => setOpenPayment(false)}
        fullScreen
        title={
          <HeadlineXS className="text-center">
            <FormattedMessage {...cart.checkout} />
          </HeadlineXS>
        }
        withDivider
      >
        {!!myCart && (
          <PaymentContainer>
            <CartPayment
              myCart={myCart}
              onSuccess={onSuccess}
              primaryBuyConfirmationOptions={
                canCloseIosWebview
                  ? {
                      actions: {
                        primary: {
                          label: <FormattedMessage {...glossary.gotIt} />,
                          onClick: closeIosWebview,
                        },
                      },
                      hideSecondaryOrTickets: true,
                    }
                  : undefined
              }
            />
          </PaymentContainer>
        )}
      </Dialog>
      <PlayerDrawer
        cardOpened={
          playerSlug ? { cardSlug: '', playerSlug: playerSlug || '' } : null
        }
        onClose={() => setPlayerSlug(null)}
      />
    </>
  );
};
